/* tslint:disable:no-parameter-reassignment */
import awaitToJs from 'await-to-js';
import * as cookies from 'js-cookie';

import {
  GET,
  POST,
  PUT,
  DELETE,
  SECURE_CONFIG,
  SECURE_CONFIG_FORM,
} from '@App/configs/rest';

import {
  MESSAGE_ERROR,
  COOKIES_AUTHORIZATION_TOKEN,
} from '@App/constantes/strings';

/**
 * Fetch
 * @class Fetch
 */

interface IAbortController {
  abort: Function,
  signal: any,
}

class Fetch {
  controller: IAbortController;
  isFetching: Boolean;

  constructor() {
    this.initialize();
  }

  /**
   * resetState
   * @memberof Fetch
   */
  initialize() {
    if (this.controller) {
      this.controller.abort();
    }

    this.isFetching = false;
    this.controller = new AbortController();
  }

  async fetch({ url = '', config = {} }: { url: string, config: any }) {
    if (this.isFetching) {
      this.stop();
    }

    this.isFetching = true;
    const { signal } = this.controller;

    if (!config.headers.Authorization || !config.headers.authorization && cookies.get(COOKIES_AUTHORIZATION_TOKEN)) {
      config.headers.authorization = (cookies.get(COOKIES_AUTHORIZATION_TOKEN) && `Bearer ${cookies.get(COOKIES_AUTHORIZATION_TOKEN)}`);

      delete config.headers.Authorization;
    }

    const [, response] = await awaitToJs(fetch(
      url,
      {
        signal,
        ...config,
      },
    ));

    this.isFetching = false;

    if (response) {
      const contentType = response!.headers.get('content-type');

      const [, data] = await awaitToJs((contentType && contentType!.includes('text/csv')) ? response!.blob() : response!.json());

      if (response!.ok) {
        return data;
      }

      if (typeof data?.message === 'object') {
        throw new Error(data.message.text);
      }

      throw new Error(data && data.message ? data.message : MESSAGE_ERROR);
    }

    return null;
  }

  post({ url = '', body = {}, headers = {} }: { url: string, body: Object, headers: any }) {
    const config = {
      body: JSON.stringify(body),
      ...SECURE_CONFIG(POST),
      headers: {
        ...headers,
      },
    };

    if (body instanceof FormData) {
      Object.assign(
        config,
        {
          body,
        },
        SECURE_CONFIG_FORM(POST),
      );
    }

    return this.fetch({ url, config });
  }

  get({ url = '', params = {}, headers = {} }: { url: string, params: any, headers: any }) {
    const config = {
      ...SECURE_CONFIG(GET),
      headers,
    };

    if (params && params.length !== 0) {
      const urlParameters = Object.keys(params)
        .filter((k) => {
          return params[k];
        })
        .map((k) => {
          return `${encodeURIComponent(k)}=${encodeURIComponent(params[k])}`;
        })
        .join('&');

      if (urlParameters) {
        url = `${url}?${urlParameters}`;
      }
    }

    return this.fetch({ url, config });
  }

  put({ url = '', body = {}, headers = {} }: { url: string, body: Object, headers: any }) {
    const overrideHeaders = Object.assign(SECURE_CONFIG(PUT));
    overrideHeaders.headers = headers;
    overrideHeaders.headers['Content-Type'] = 'application/json';

    const config = {
      body: JSON.stringify(body),
      ...overrideHeaders,
    };

    if (body instanceof FormData) {
      Object.assign(
        config,
        {
          body,
        },
        SECURE_CONFIG_FORM(POST),
      );
    }

    return this.fetch({ url, config });
  }

  delete({ url = '', params = {}, body = {} }: { url: string, params: any, body: any}) {
    const config = {
      ...SECURE_CONFIG(DELETE),
      body: (body instanceof FormData) ? body : JSON.stringify(body),
    };

    if (params && params.length !== 0) {
      const urlParameters = Object.keys(params)
        .filter((k) => {
          return params[k];
        })
        .map((k) => {
          return `${encodeURIComponent(k)}=${encodeURIComponent(params[k])}`;
        })
        .join('&');

      if (urlParameters) {
        url = `${url}?${urlParameters}`;
      }
    }

    return this.fetch({ url, config });
  }

  /**
   * stop
   * @memberof Fetch
   */
  stop() {
    this.initialize();
  }
}

export default Fetch;
